import { formatLog } from './utils';

/**
 * A custom logger that outputs messages only when running in a backend environment,
 * e.g., NestJS, or when debug mode is explicitly enabled in the browser.
 */
export class Logger {
  /**
   * Indicates whether the current runtime environment is a backend, e.g., NestJS.
   * Determined by the absence of the `window` object.
   */
  IS_BACKEND_ENV = globalThis.window === undefined;

  /**
   * Key used to persist the debug mode state to `localStorage`.
   */
  DEBUG_KEY = 'debug_mode';

  /**
   * Logs a debug message to the console, if called in a backend environment, i.e., NestJS,
   * or if the debug mode is enabled.
   *
   * @param messageParts  Message segments to be logged. Objects are formatted automatically.
   */
  debug(...messageParts: unknown[]): void {
    if (this.areDebugLogsEnabled()) {
      console.log(formatLog(messageParts));
    }
  }

  /**
   * Enables debug mode by setting the `debug_mode` flag in `localStorage` to `true`.
   * Does nothing if called in a backend environment.
   */
  enableDebugMode(): void {
    if (!this.IS_BACKEND_ENV) {
      localStorage.setItem(this.DEBUG_KEY, 'true');

      console.log(
        '%c[DEBUG] Debug mode enabled. Debug logs will now appear in the console.',
        'color: green; font-weight: bold;'
      );
    }
  }

  /**
   * Disables debug mode by removing the `debug_mode` flag from `localStorage`.
   * Does nothing if called in a backend environment.
   */
  disableDebugMode(): void {
    if (!this.IS_BACKEND_ENV) {
      localStorage.removeItem(this.DEBUG_KEY);

      console.log('%c[DEBUG] Debug mode disabled.', 'color: orange; font-weight: bold;');
    }
  }

  /**
   * Returns whether debug mode is enabled, i.e., the `debug_mode` key in `localStorage` is set to `true`.
   */
  isDebugModeEnabled(): boolean {
    try {
      return localStorage.getItem(this.DEBUG_KEY) === 'true';
    } catch {
      // localStorage can be disabled in some environments, e.g., incognito mode, NestJS
      return false;
    }
  }

  /**
   * Returns whether debug logs should be logged to the console.
   */
  private areDebugLogsEnabled(): boolean {
    return this.IS_BACKEND_ENV || this.isDebugModeEnabled();
  }
}
